import * as React from 'react';
import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { formGroupConnect } from '@groove-labs/groove-ui';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import ReactSelect from 'react-select';

import useStore from 'Modules/App/Store/useStore';
import useMoboEnabled from 'Modules/Shared/hooks/useMoboEnabled';
import useMoboDropdownOptions from 'Modules/Shared//hooks/useMoboDropdownOptions';
import { trackEvent } from 'Utils/segment';
import { ANALYTICS_BASE_PROPERTIES } from 'Modules/Shared/sagas/analytics';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    fontSize: '11px',
    paddingLeft: '20px',
  },
  container: {
    display: 'flex',
    width: '200px',
    height: '5px',
    color: '#000000',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  label: {
    flexDirection: 'column',
    minWidth: '57.25px',
  },
}));

const customReactSelectOptions = {
  control: (base: CSSProperties) => ({
    ...base,
    backgroundColor: 'white',
    border: 'none',
    borderBottom: '1px solid black',
    borderRadius: 0,
    boxShadow: 'none',
    boxSizing: 'content-box',
    flex: 1,
    height: 36,
    minHeight: 36,
    overflowY: 'auto',
    '&:focus': {
      height: 36,
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  container: (base: CSSProperties) => ({
    ...base,
    height: 36,
    minWidth: '90%',
  }),
  menu: (base: CSSProperties) => ({
    ...base,
    marginTop: 0,
    position: 'absolute',
    zIndex: 7,
  }),
};

const MoboDropdown: React.FC = () => {
  const { root, label, container } = useStyles({});
  const [value, setValue] = useState<Record<string, string>>();
  const moboUser = useStore(state => state.moboUser);
  const setMoboUser = useStore(state => state.setMoboUser);
  const isMoboEnabled = useMoboEnabled();
  const [searchText, setSearchText] = useState('');
  const options = useMoboDropdownOptions(searchText);
  const sortedOptions = options.sort((a, b) => a.label.localeCompare(b.label));

  /**
   * When the moboUser is cleared, also clear out the value from the picklist
   */
  useEffect(() => {
    if (!moboUser) {
      setValue(undefined);
    }
  }, [moboUser]);

  const handleChange = (option?: Record<string, string>) => {
    if (option) {
      const id = option.value;
      const mobo = options.find(o => o.value === id);
      if (!mobo) {
        return;
      }

      setMoboUser({
        id: mobo.value,
        name: mobo.label,
        email: mobo.email,
      });

      trackEvent('Clicked MOBO Drop Down', {
        ...ANALYTICS_BASE_PROPERTIES,
        featureCategory: 'mobo',
      });
    } else {
      setMoboUser(undefined);
    }

    setValue(option);
  };

  const updateSearchText = (input: string) => {
    if (input !== searchText) {
      setSearchText(input);
    }
  };

  if (!isMoboEnabled) {
    return null;
  }

  return (
    <div className={root}>
      <div className={label}>
        <div>Manage on</div>
        <div>Behalf of: </div>
      </div>
      <div className={container}>
        <ReactSelect
          isClearable
          options={sortedOptions}
          styles={customReactSelectOptions}
          value={value || null}
          onInputChange={updateSearchText}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

export default formGroupConnect()(MoboDropdown);
